
import { supabase } from '../client';
import { GiftCardType } from '../types';
import { addDays } from 'date-fns';

// Generate a random 6-character code
const generateGiftCardCode = () => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return code;
};

// Create a new gift card
export const createGiftCard = async (
  firstName: string,
  lastName: string,
  totalClasses: number = 2
): Promise<{ success: boolean; data?: GiftCardType; error?: string }> => {
  try {
    // Generate a random code
    let code = generateGiftCardCode();
    let codeExists = true;
    
    // Keep generating new codes until we find one that doesn't exist
    while (codeExists) {
      const { data } = await supabase
        .from('gift_cards')
        .select('code')
        .eq('code', code)
        .single();
        
      codeExists = !!data;
      if (codeExists) {
        code = generateGiftCardCode();
      }
    }
    
    // Calculate expiration date (2 weeks from now)
    const expirationDate = addDays(new Date(), 14);
    
    const { data, error } = await supabase
      .from('gift_cards')
      .insert({
        code,
        first_name: firstName,
        last_name: lastName,
        total_classes: totalClasses,
        remaining_classes: totalClasses,
        expiration_date: expirationDate.toISOString(),
        is_active: true
      })
      .select('*')
      .single();
      
    if (error) throw error;
    
    return {
      success: true,
      data: {
        id: data.id,
        code: data.code,
        firstName: data.first_name,
        lastName: data.last_name,
        totalClasses: data.total_classes,
        remainingClasses: data.remaining_classes,
        createdAt: data.created_at,
        expirationDate: data.expiration_date,
        isActive: data.is_active
      }
    };
  } catch (error: any) {
    console.error('Error creating gift card:', error);
    return { success: false, error: error.message || 'Failed to create gift card' };
  }
};

// Get all gift cards (for admin dashboard)
export const getAllGiftCards = async (): Promise<{
  success: boolean;
  data?: GiftCardType[];
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('gift_cards')
      .select('*')
      .order('created_at', { ascending: false });
      
    if (error) throw error;
    
    const formattedData = data.map(card => ({
      id: card.id,
      code: card.code,
      firstName: card.first_name,
      lastName: card.last_name,
      totalClasses: card.total_classes,
      remainingClasses: card.remaining_classes,
      createdAt: card.created_at,
      expirationDate: card.expiration_date,
      isActive: card.is_active
    }));
    
    return { success: true, data: formattedData };
  } catch (error: any) {
    console.error('Error fetching gift cards:', error);
    return { success: false, error: error.message || 'Failed to fetch gift cards' };
  }
};

// Verify a gift card by code
export const verifyGiftCard = async (code: string): Promise<{
  success: boolean;
  data?: GiftCardType;
  error?: string;
}> => {
  try {
    // First check if the card exists
    const { data: cardCheck, error: checkError } = await supabase
      .from('gift_cards')
      .select('id')
      .eq('code', code.toUpperCase());
      
    if (checkError) throw checkError;
    
    if (!cardCheck || cardCheck.length === 0) {
      return { success: false, error: 'Gift card not found' };
    }
    
    // Now get the full details using maybeSingle() to avoid the JSON error
    const { data, error } = await supabase
      .from('gift_cards')
      .select('*')
      .eq('code', code.toUpperCase())
      .eq('is_active', true)
      .maybeSingle();
      
    if (error) throw error;
    
    if (!data) {
      return { success: false, error: 'Inactive gift card' };
    }
    
    // Check if expired
    const now = new Date();
    const expirationDate = new Date(data.expiration_date);
    
    if (now > expirationDate) {
      return { success: false, error: 'Gift card has expired' };
    }
    
    // Check if all classes have been used
    if (data.remaining_classes <= 0) {
      return { success: false, error: 'Gift card has no remaining classes' };
    }
    
    return {
      success: true,
      data: {
        id: data.id,
        code: data.code,
        firstName: data.first_name,
        lastName: data.last_name,
        totalClasses: data.total_classes,
        remainingClasses: data.remaining_classes,
        createdAt: data.created_at,
        expirationDate: data.expiration_date,
        isActive: data.is_active
      }
    };
  } catch (error: any) {
    console.error('Error verifying gift card:', error);
    return { success: false, error: error.message || 'Invalid gift card code' };
  }
};

// Use a gift card for booking
export const useGiftCard = async (
  giftCardId: string,
  classId: string,
  firstName: string,
  lastName: string,
  email: string,
  phone: string,
  groupSize: number = 1,
  groupNames: string[] = []
): Promise<{ success: boolean; bookingId?: string; error?: string }> => {
  try {
    // Start a transaction
    const { data: giftCard, error: giftCardError } = await supabase
      .from('gift_cards')
      .select('*')
      .eq('id', giftCardId)
      .eq('is_active', true)
      .maybeSingle();
      
    if (giftCardError) throw giftCardError;
    
    if (!giftCard) {
      return { success: false, error: 'Gift card not found or inactive' };
    }
    
    if (giftCard.remaining_classes <= 0) {
      return { success: false, error: 'Gift card has no remaining classes' };
    }
    
    // Check if expired
    const now = new Date();
    const expirationDate = new Date(giftCard.expiration_date);
    
    if (now > expirationDate) {
      return { success: false, error: 'Gift card has expired' };
    }
    
    // Update class availability
    const { data: classData, error: classError } = await supabase
      .from('classes')
      .select('availableSpots')
      .eq('id', classId)
      .maybeSingle();
      
    if (classError) throw classError;
    
    if (!classData) {
      return { success: false, error: 'Class not found' };
    }
    
    if (classData.availableSpots < groupSize) {
      return { success: false, error: 'Not enough available spots for this booking' };
    }
    
    // Update gift card with new remaining classes
    const { error: updateGiftCardError } = await supabase
      .from('gift_cards')
      .update({ remaining_classes: giftCard.remaining_classes - 1 })
      .eq('id', giftCardId);
      
    if (updateGiftCardError) throw updateGiftCardError;
    
    // Update class availability
    const { error: updateClassError } = await supabase
      .from('classes')
      .update({ availableSpots: classData.availableSpots - groupSize })
      .eq('id', classId);
      
    if (updateClassError) throw updateClassError;
    
    // Create booking
    const { data: booking, error: bookingError } = await supabase
      .from('bookings')
      .insert({
        class_id: classId,
        first_name: firstName,
        last_name: lastName,
        email: email,
        phone: phone,
        booking_type: 'gift_card',
        gift_card_id: giftCardId,
        group_size: groupSize,
        group_names: groupNames.length > 0 ? groupNames : null
      })
      .select('id')
      .single();
      
    if (bookingError) throw bookingError;
    
    return { success: true, bookingId: booking.id };
    
  } catch (error: any) {
    console.error('Error using gift card:', error);
    return { success: false, error: error.message || 'Failed to use gift card for booking' };
  }
};
